import React, { useState, useRef, useEffect } from 'react';
import { Upload, Printer, Save, Search, Loader2, Sparkles, CheckCircle } from 'lucide-react';
import type { PrintableRef, Currency } from '@/types';
import PrintableDocument from '@/components/PrintableDocument';
import InvoicesService from '@/api/invoices.service';
import PaymentMethodsService from '@/api/paymentmethods.service';
import ProductServicesService from '@/api/productservices.service';
import ChartOfAccountsService from '@/api/chartofaccounts.service';
import TransactionsService from '@/api/transactions.service';
import TaxesService from '@/api/taxes.service';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import FormModal from '@/components/FormModal';
import BillToSection from '@/components/BillToSection';
import { InvoiceTaxSection } from '@/components/tax/InvoiceTaxSection';
import { DocumentUpload } from '@/components/DocumentUpload';

// Update the Customer type to match the schema
type Customer = {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  street: string;
  city: string;
  state: string;
  zipCode?: string; // Optional
  country: string;
  companyName?: string; // Optional
};

type ProductService = {
  id: number;
  name: string;
  description: string;
  unitPrice: number;
};

// Update the Tax interface to match our new components
interface Tax {
  id: number;
  taxName: string;
  taxRate: number;
  country?: string;
  type?: string;
  code?: string;
  taxAmount?: number;
}

export default function DocumentForm() {
  const [logo, setLogo] = useState<string>();
  const [signature, setSignature] = useState<string>();
  const [currency, setCurrency] = useState<Currency>('USD');
  const [items, setItems] = useState([{ productServiceId: 0, name: '', description: '', quantity: 1, unitPrice: 0 }]);
  const [status] = useState('pending');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    emailAddress: '',
    phoneNumber: '',
    logoImg: ''
  });
  const [customerDetails, setCustomerDetails] = useState<Customer>({
    id: 0,
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailAddress: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    companyName: ''
  });
  const [customerData, setCustomerData] = useState<Customer[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [isInclusive, setIsInclusive] = useState(false);
  const [invoiceId, setInvoiceId] = useState(0);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isMarking, setIsMarking] = useState(false);
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isPaymentMethodsLoaded, setIsPaymentMethodsLoaded] = useState(false);
  
  // Add chart of accounts state
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [isChartOfAccountsLoaded, setIsChartOfAccountsLoaded] = useState(false);
  
  const [searchProduct, setSearchProduct] = useState('');
  const [isSearchingProduct, setIsSearchingProduct] = useState(false);
  const [productData, setProductData] = useState<ProductService[]>([]);

  const printableRef = useRef<PrintableRef>(null);
  
  const [isSaving, setIsSaving] = useState(false);

  const { toast } = useToast();
  const showToastWithTimeout = (toastType: string, title: string, description: string, duration: number) => {
    toast({
      title: title,
      description: description,
      variant: toastType === "error" ? "destructive" : "default",
      duration: duration,
    });
  };

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  // Add transaction recording states
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [isRecordingTransaction, setIsRecordingTransaction] = useState(false);
  const [transactionType, setTransactionType] = useState('DEBIT');

  const [businessTaxInfos, setBusinessTaxInfos] = useState<any[]>([]);
  const [customerTaxInfos, setCustomerTaxInfos] = useState<any[]>([]);

  // AI Document Processing states
  const [showAiTab, setShowAiTab] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [isApplyingExtraction, setIsApplyingExtraction] = useState(false);

  useEffect(() => {
    if (selectedBusiness) {
      setCompanyDetails({
        name: selectedBusiness.name,
        address: selectedBusiness.address,
        city: selectedBusiness.city,
        country: selectedBusiness.country,
        emailAddress: selectedBusiness.emailAddress,
        phoneNumber: selectedBusiness.phoneNumber,
        logoImg: selectedBusiness.logoImg
      });
      setLogo(selectedBusiness.logoImg);
      
      // Fetch chart of accounts for the business
      fetchChartOfAccounts();
      
      // Fetch business tax information
      fetchBusinessTaxInfos(selectedBusiness.id);
    }
  }, [selectedBusiness]);

  // Watch for changes to customerDetails.id and fetch customer tax info
  useEffect(() => {
    if (customerDetails?.id > 0) {
      fetchCustomerTaxInfos(customerDetails.id);
    }
  }, [customerDetails?.id]);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await PaymentMethodsService.getAllPaymentMethods();
        if (response.data.result === 'success') {
          setPaymentMethods(response.data.paymentMethods);
        } else {
          showToastWithTimeout("error", "Error", "Failed to fetch payment methods", 3000);
        }
      } catch (error) {
        showToastWithTimeout("error", "Error", "Failed to fetch payment methods", 3000);
      } finally {
        setIsPaymentMethodsLoaded(true);
      }
    };

    fetchPaymentMethods();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchProduct.length > 0) {
        handleSearchProduct();
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchProduct]);

  const handleSearchProduct = async () => {
    setIsSearchingProduct(true);
    try {
      const response = await ProductServicesService.searchProductService(selectedBusiness.id, searchProduct);
      const { data } = response;
      if (data.result === 'success') {
        setProductData(data.productServices);
        showToastWithTimeout("success", "✅ Product Search Successful", "Product search successful", 3000);
      } else {
        showToastWithTimeout("error", "❌ Product Search Failed", data.message, 3000);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSearchingProduct(false);
    }
  };

  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignature(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePrint = () => {
    if (printableRef.current) {
      printableRef.current.print();
    }
  };

  const handleMarkAsPaid = () => {
    if (!paymentMethodId) {
      showToastWithTimeout("error", "Error", "Please select a payment method", 3000);
      return;
    }
    updateInvoiceStatus('paid', calculateTotal());
    setModalOpen(false);
  };

  const handleMarkAsPartiallyPaid = () => {
    if (!paymentMethodId) {
      showToastWithTimeout("error", "Error", "Please select a payment method", 3000);
      return;
    }
    updateInvoiceStatus('partially_paid', amountPaid);
    setModalOpen(false);
  };

  const updateInvoiceStatus = async (status: string, amount: any) => {
    try {
      setIsMarking(true);
      const payload = {
        status,
        amount,
        paymentMethodId,
        paymentDate,
        paymentReference
      };

      const response = await InvoicesService.updateSpecificInvoice(invoiceId, payload);
      if (response.data.result === 'success') {
        // Record the transaction if an account is selected
        if (selectedAccountId) {
          await recordTransaction(status, amount, response.data.receiptNumber, response.data.receiptId);
        }
        
        showToastWithTimeout(
          "default", 
          `✅ Marked as ${status.replace('_', ' ').toUpperCase()}`,
          `Invoice #${invoiceNumber} has been marked as ${status.replace('_', ' ')}${amount ? ` with amount ${amount}` : ''} and Receipt #${response.data.receiptNumber} has been created.`,
          5000
        );
        setTimeout(() => {
          if (status === 'paid') window.location.href = `/receipt/${response.data.receiptId}`;
          //else if (status === 'partially_paid') window.location.href = `/invoices`;
        }, 2000);
      } else {
        showToastWithTimeout("error", "Error", response.data.message, 3000);
      }
    } catch (error) {
      console.log("updateSpecificInvoice error", error);
      showToastWithTimeout("error", "Error", "Failed to update invoice status", 3000);
    } finally {
      setIsMarking(false);
    }
  };
  
  const recordTransaction = async (status: string, amount: any, receiptNumber: string, receiptId: string) => {
    try {
      // Convert to number and ensure it's in cents for storage
      const amountValue = typeof amount === 'string' ? parseFloat(amount) : amount;
      
      const transactionData = {
        chartOfAccountId: parseInt(selectedAccountId),
        transactionType: "CREDIT", // Receipt is always a credit to the selected account
        amount: amountValue,
        currency: currency,
        description: `Payment received for Invoice #${invoiceNumber}`,
        reference: receiptNumber,
        referenceId: receiptId,
        referenceType: "receipt",
        transactionDate: paymentDate,
      };
      
      const response = await TransactionsService.createTransaction(transactionData, selectedBusiness.id);
      
      if (response.data.result === 'success') {
        showToastWithTimeout("success", "✅ Transaction Recorded", "The payment has been recorded in the accounting system.", 3000);
      } else {
        showToastWithTimeout("error", "❌ Transaction Failed", response.data.message, 3000);
      }
    } catch (error) {
      console.error("Error recording transaction:", error);
      showToastWithTimeout("error", "❌ Transaction Failed", "An error occurred while recording the transaction.", 3000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    if (!(companyDetails && customerDetails)) {
      showToastWithTimeout("error", "❌ Invoice Creation Failed", "Please fill in all required fields.", 3000);
      setIsSaving(false);
      return;
    }

    try {
      const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0) || 0;
      const taxAmount = taxes.reduce((sum, tax) => sum + (tax.taxAmount || 0), 0);
      const total = subtotal + taxAmount;
      
      const newInvoice = {
        customerId: customerDetails.id,
        amount: subtotal,
        total: total,
        currency,
        status,
        issueDate,
        dueDate,
        items: items.map(item => ({
          productServiceId: item.productServiceId,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalPrice: item.quantity * item.unitPrice,
        })),
        taxes,
        isInclusive,
        companyDetails,
        customerDetails,
        notes,
        terms,
        logo,
        signature,
        businessTaxInfos,
        customerTaxInfos
      };

      const response = await InvoicesService.createInvoice(newInvoice, selectedBusiness.id);
      console.log("createInvoice response.data", response.data);
      if (response.data.result === "success") {
        showToastWithTimeout("success", "✅ Invoice Created", "Invoice created successfully", 3000);
        setInvoiceNumber(response.data.invoiceNumber);
        setInvoiceId(response.data.invoiceId);
        // Show transaction modal after successful invoice creation
        setShowTransactionModal(true);
      } else {
        showToastWithTimeout("error", "❌ Invoice Creation Failed", response.data.message, 3000);
      }
    } catch (error) {
      console.error(error);
      showToastWithTimeout("error", "❌ Invoice Creation Failed", "An error occurred while creating the invoice.", 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // Add a function to handle tax changes
  const handleTaxesChange = (newTaxes: Tax[]) => {
    setTaxes(newTaxes);
  };

  const handleInclusiveChange = (newIsInclusive: boolean) => {
    setIsInclusive(newIsInclusive);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  const calculateTotalTax = () => {
    return taxes.reduce((sum, tax) => sum + (tax.taxAmount || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTotalTax();
  };

  const fetchChartOfAccounts = async () => {
    try {
      if (!selectedBusiness) return;
      const response = await ChartOfAccountsService.getAllChartOfAccounts(selectedBusiness.id);
      if (response.data.result === 'success') {
        // Filter to include only active accounts
        const activeAccounts = response.data.chartOfAccounts.filter(
          (account: any) => account.isActive
        );
        setChartOfAccounts(activeAccounts);
      } else {
        showToastWithTimeout("error", "Error", "Failed to fetch chart of accounts", 3000);
      }
    } catch (error) {
      showToastWithTimeout("error", "Error", "Failed to fetch chart of accounts", 3000);
    } finally {
      setIsChartOfAccountsLoaded(true);
    }
  };

  const recordInvoiceTransaction = async () => {
    try {
      setIsRecordingTransaction(true);
      
      if (!selectedAccountId) {
        showToastWithTimeout("error", "Error", "Please select an account", 3000);
        setIsRecordingTransaction(false);
        return;
      }
      
      const transactionData = {
        chartOfAccountId: parseInt(selectedAccountId),
        transactionType: transactionType,
        amount: calculateTotal(),
        currency: currency,
        description: `Invoice #${invoiceNumber} issued to ${customerDetails.firstName} ${customerDetails.lastName}${customerDetails.companyName ? ` (${customerDetails.companyName})` : ''}`,
        reference: invoiceNumber,
        referenceId: invoiceId.toString(),
        referenceType: "invoice",
        transactionDate: issueDate,
      };
      
      const response = await TransactionsService.createTransaction(transactionData, selectedBusiness.id);
      
      if (response.data.result === 'success') {
        showToastWithTimeout("success", "✅ Transaction Recorded", "The invoice has been recorded in the accounting system.", 3000);
        setShowTransactionModal(false);
      } else {
        showToastWithTimeout("error", "❌ Transaction Failed", response.data.message, 3000);
      }
    } catch (error) {
      console.error("Error recording transaction:", error);
      showToastWithTimeout("error", "❌ Transaction Failed", "An error occurred while recording the transaction.", 3000);
    } finally {
      setIsRecordingTransaction(false);
    }
  };

  // Add function to fetch customer tax information
  const fetchCustomerTaxInfos = async (customerId: number) => {
    try {
      const response = await TaxesService.getCustomerTaxInfos(customerId);
      console.log("getCustomerTaxInfos response.data", response.data);
      if (response.data.result === 'success') {
        setCustomerTaxInfos(response.data.customerTaxInfos || []);
      }
    } catch (error) {
      console.error('Error fetching customer tax information:', error);
    }
  };

  // Add function to fetch business tax information
  const fetchBusinessTaxInfos = async (businessId: number) => {
    try {
      const response = await TaxesService.getBusinessTaxInfos(businessId);
      if (response.data.result === 'success') {
        setBusinessTaxInfos(response.data.businessTaxInfos || []);
      }
    } catch (error) {
      console.error('Error fetching business tax information:', error);
    }
  };

  // Handle AI extraction completion
  const handleExtractionComplete = (data: any) => {
    setExtractedData(data);
    setShowAiTab(true);
  };

  // Apply extracted data to form
  const applyExtractedData = () => {
    if (!extractedData) return;
    
    setIsApplyingExtraction(true);
    
    try {
      // Apply vendor/customer information
      if (extractedData.vendorName) {
        // Try to find matching customer
        const matchingCustomer = customerData.find(customer => 
          customer.companyName?.toLowerCase().includes(extractedData.vendorName.toLowerCase()) ||
          `${customer.firstName} ${customer.lastName}`.toLowerCase().includes(extractedData.vendorName.toLowerCase())
        );
        
        if (matchingCustomer) {
          setCustomerDetails(matchingCustomer);
        } else {
          // Create new customer data from extraction
          setCustomerDetails(prev => ({
            ...prev,
            companyName: extractedData.vendorName,
            street: extractedData.vendorAddress || prev.street
          }));
        }
      }

      // Apply invoice number
      if (extractedData.invoiceNumber) {
        setInvoiceNumber(extractedData.invoiceNumber);
      }

      // Apply dates
      if (extractedData.invoiceDate) {
        const date = new Date(extractedData.invoiceDate);
        if (!isNaN(date.getTime())) {
          setIssueDate(date.toISOString().split('T')[0]);
        }
      }

      // Apply currency
      if (extractedData.currency) {
        setCurrency(extractedData.currency as Currency);
      }

      // Apply line items
      if (extractedData.lineItems && extractedData.lineItems.length > 0) {
        const newItems = extractedData.lineItems.map((item: any) => ({
          productServiceId: 0,
          name: item.description || '',
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0
        }));
        setItems(newItems);
      } else if (extractedData.totalAmount) {
        // If no line items but total amount, create a single item
        setItems([{
          productServiceId: 0,
          name: 'Extracted Item',
          description: 'Item extracted from document',
          quantity: 1,
          unitPrice: extractedData.totalAmount
        }]);
      }

      // Apply tax information
      if (extractedData.taxAmount && extractedData.totalAmount) {
        const taxRate = (extractedData.taxAmount / (extractedData.totalAmount - extractedData.taxAmount)) * 100;
        setTaxes([{
          id: 0,
          taxName: 'Extracted Tax',
          taxRate: taxRate,
          taxAmount: extractedData.taxAmount
        }]);
      }

      toast({
        title: "Data Applied",
        description: "Extracted data has been applied to the form",
      });
    } catch (error) {
      console.error('Error applying extracted data:', error);
      toast({
        title: "Error",
        description: "Failed to apply extracted data",
        variant: "destructive",
      });
    } finally {
      setIsApplyingExtraction(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold capitalize">Create Invoice</h1>
        <div className="flex gap-4">
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="USD">USD ($)</option>
            <option value="ZWG">ZWG ($)</option>
            <option value="ZAR">ZAR (R)</option>
            <option value="MZN">MZN (MZN)</option>
          </select>
          <Button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 rounded-lg"
            disabled={!invoiceNumber}
          >
            <Printer size={20} />
            Print
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-4 py-2 rounded-lg"
            disabled={invoiceNumber !== '' || isSaving}
          >
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save size={20} />Save</>}
          </Button>
          {invoiceNumber && (
            <>
              <Button 
                onClick={() => setModalOpen(true)} 
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
                disabled={isMarking}
              >
                {isMarking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark"}
              </Button>
              <Button
                onClick={() => setShowTransactionModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
              >
                Record Transaction
              </Button>
            </>
          )}
        </div>
      </div>

      <FormModal open={isModalOpen} setOpen={setModalOpen} title="Mark Invoice">
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Amount Paid</label>
          <Input
            type="number"
            value={amountPaid}
            onChange={(e) => setAmountPaid(e.target.value)}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Payment Method</label>
          {isPaymentMethodsLoaded ? (
            <select
              value={paymentMethodId}
              onChange={(e) => setPaymentMethodId(e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">Select Method</option>
              {paymentMethods && paymentMethods.map((method: any) => (
                <option key={method.id} value={method.id}>{method.name}</option>
              ))}
            </select>
          ) : (
            <div className="flex justify-center items-center">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading payment methods...
            </div>
          )}
        </div>
        {paymentMethodId === '4' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">Payment Reference</label>
            <Input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              placeholder="Enter payment reference"
            />
          </div>
        )}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Payment Date</label>
          <Input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Chart of Account</label>
          {isChartOfAccountsLoaded ? (
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">Select Account (Optional)</option>
              {chartOfAccounts.map((account: any) => (
                <option key={account.id} value={account.id}>
                  {account.code} - {account.name} ({account.type})
                </option>
              ))}
            </select>
          ) : (
            <div className="flex justify-center items-center">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Loading accounts...
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Select an account to record this payment as a transaction
          </p>
        </div>
        <div className="flex justify-end space-x-2">
          <Button onClick={handleMarkAsPaid} className="bg-green-500/90 text-green-50 hover:bg-green-500" disabled={amountPaid !== ''}>Mark as Fully Paid</Button>
          <Button onClick={handleMarkAsPartiallyPaid} className="bg-yellow-600 hover:bg-yellow-700 text-white" disabled={amountPaid === ''}>Mark as Partially Paid</Button>
          <Button onClick={() => setModalOpen(false)} className="bg-red-500 hover:bg-red-600 text-white">Cancel</Button>
        </div>
      </FormModal>

      {/* Transaction Recording Modal */}
      <FormModal open={showTransactionModal} setOpen={setShowTransactionModal} title="Record Invoice Transaction">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Chart of Account</label>
            {isChartOfAccountsLoaded ? (
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Account</option>
                {chartOfAccounts.map((account: any) => (
                  <option key={account.id} value={account.id}>
                    {account.code} - {account.name} ({account.type})
                  </option>
                ))}
              </select>
            ) : (
              <div className="flex justify-center items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading accounts...
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Select "Accounts Receivable" to record this invoice as an asset
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Transaction Type</label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="DEBIT">DEBIT (For Accounts Receivable)</option>
              <option value="CREDIT">CREDIT</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              For Accounts Receivable, typically use DEBIT as invoices represent money owed to your business
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Amount</label>
            <div className="flex items-center">
              <span className="mr-2">{currency}</span>
              <Input
                type="text"
                value={calculateTotal().toFixed(2)}
                readOnly
                className="bg-gray-100"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Input
              type="text"
              value={`Invoice #${invoiceNumber} issued to ${customerDetails.firstName} ${customerDetails.lastName}${customerDetails.companyName ? ` (${customerDetails.companyName})` : ''}`}
              readOnly
              className="bg-gray-100"
            />
          </div>
          
          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              onClick={recordInvoiceTransaction} 
              className="bg-indigo-500 hover:bg-indigo-600 text-white"
              disabled={isRecordingTransaction || !selectedAccountId}
            >
              {isRecordingTransaction ? 
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recording...</> : 
                'Record Transaction'}
            </Button>
            <Button onClick={() => setShowTransactionModal(false)} className="bg-gray-500 hover:bg-gray-600 text-white">
              Cancel
            </Button>
          </div>
        </div>
      </FormModal>

      {/* Main Form with Tabs */}
      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="ai">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Processing
          </TabsTrigger>
          {showAiTab && (
            <TabsTrigger value="extracted">
              Extracted Data
              {extractedData && <span className="ml-2 w-2 h-2 bg-green-500 rounded-full"></span>}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="manual" className="space-y-6">
          <BillToSection
            customerDetails={customerDetails}
            setCustomerDetails={setCustomerDetails}
            customerData={customerData}
            setCustomerData={setCustomerData}
          />

          {customerTaxInfos.length > 0 && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold mb-2 text-gray-700">Customer Tax Information</h3>
              <div className="space-y-2">
                {customerTaxInfos.map((taxInfo, index) => (
                  <div key={index} className="flex items-center text-sm">
                    <span className="font-medium mr-2">{taxInfo.taxType?.name || 'Tax'}:</span>
                    <span className="text-gray-600">{taxInfo.taxIdentifier}</span>
                    {taxInfo.isVerified && (
                      <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                        Verified
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Dates</h2>
            <div className="flex gap-4 items-center">
              <label className="block">
                Issue Date:
                <Input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={issueDate}
                  onChange={(e: any) => setIssueDate(e.target.value)}
                />
              </label>
              <label className="block">
                Due Date:
                <Input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={dueDate}
                  onChange={(e: any) => setDueDate(e.target.value)}
                />
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Items</h2>
            <div className='flex items-center my-4'>
              <Input
                type="text"
                placeholder="Search for product/service"
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
                className="mr-2"
              />
              <Button onClick={handleSearchProduct} disabled={isSearchingProduct}>
                {isSearchingProduct ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {productData.length > 0 && (
              <select
                onChange={(e) => {
                  const selectedProduct = productData.find((product) => product.id === Number(e.target.value));
                  if (selectedProduct) {
                    // Check if the last item is empty (default values)
                    const lastItem = items[items.length - 1];
                    const isLastItemEmpty = 
                      lastItem.productServiceId === 0 && 
                      lastItem.name === '' && 
                      lastItem.description === '' && 
                      lastItem.quantity === 1 && 
                      lastItem.unitPrice === 0;
                    
                    const newItems = [...items];
                    
                    if (isLastItemEmpty) {
                      // Replace the empty item
                      newItems[items.length - 1] = {
                        productServiceId: selectedProduct.id,
                        name: selectedProduct.name,
                        description: selectedProduct.description,
                        quantity: 1,
                        unitPrice: selectedProduct.unitPrice
                      };
                    } else {
                      // Append a new item
                      newItems.push({
                        productServiceId: selectedProduct.id,
                        name: selectedProduct.name,
                        description: selectedProduct.description,
                        quantity: 1,
                        unitPrice: selectedProduct.unitPrice
                      });
                    }
                    setItems(newItems);
                    setProductData([]);
                    setSearchProduct('');
                  }
                }}
                className="w-full p-2 border rounded-lg bg-white shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
              >
                <option value="" disabled selected>Select a product/service</option>
                {productData.map((product) => (
                  <option
                    key={product.id}
                    value={String(product.id)}
                    className="p-2 border-b border-gray-200 hover:bg-gray-100"
                  >
                    {product.name} (${product.unitPrice})
                  </option>
                ))}
              </select>
            )}
            {items.map((item, index) => (
              <div key={index} className="flex gap-4 items-center">
                <Input
                  type="text"
                  placeholder="Name"
                  className="flex-1 p-2 border rounded"
                  value={item.name}
                  onChange={(e: any) => {
                    const newItems = [...items];
                    newItems[index].name = e.target.value;
                    setItems(newItems);
                  }}
                />
                <Textarea
                  placeholder="Description"
                  className="flex-1 p-2 border rounded"
                  value={item.description}
                  onChange={(e: any) => {
                    const newItems = [...items];
                    newItems[index].description = e.target.value.slice(0, 255);
                    setItems(newItems);
                  }}
                  maxLength={255}
                />
                <Input
                  type="number"
                  placeholder="Quantity"
                  className="w-24 p-2 border rounded"
                  value={item.quantity}
                  onChange={(e: any) => {
                    const newItems = [...items];
                    newItems[index].quantity = Number(e.target.value);
                    setItems(newItems);
                  }}
                />
                <div className="flex items-center">
                  <span className="mx-2">{currency}</span>
                  <Input
                    type="number"
                    placeholder="Price"
                    className="w-32 p-2 border rounded"
                    value={item.unitPrice}
                    onChange={(e: any) => {
                      const newItems = [...items];
                      newItems[index].unitPrice = Number(e.target.value);
                      setItems(newItems);
                    }}
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => {
                    const newItems = items.filter((_: any, i: any) => i !== index);
                    setItems(newItems);
                  }}
                  className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  -
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setItems((prev: any) => [
                      ...prev,
                      { productServiceId: 0, name: '', description: '', quantity: 1, unitPrice: 0 },
                    ])
                  }}
                  className="px-2 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  +
                </Button>
              </div>
            ))}
          </div>

          <div className="space-y-4 mt-8">
            <h2 className="text-lg font-semibold">Taxes</h2>
            <InvoiceTaxSection
              subtotal={calculateSubtotal()}
              currency={currency}
              businessId={selectedBusiness?.id}
              customerId={customerDetails?.id}
              initialTaxes={taxes}
              onTaxesChange={handleTaxesChange}
              isInclusive={isInclusive}
              onInclusiveChange={handleInclusiveChange}
            />
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Additional Details</h2>
            <Textarea
              placeholder="Notes"
              className="w-full p-2 border rounded"
              value={notes}
              onChange={(e: any) => setNotes(e.target.value)}
            />
            <Textarea
              placeholder="Terms and Conditions"
              className="w-full p-2 border rounded"
              value={terms}
              onChange={(e: any) => setTerms(e.target.value)}
            />
          </div>

          <div>
            <label className="block mb-2">
              Upload Signature
              <div className="mt-1 flex items-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  className="hidden"
                  id="signature-upload"
                />
                <label
                  htmlFor="signature-upload"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200"
                >
                  <Upload size={20} />
                  Choose Signature
                </label>
              </div>
            </label>
            {signature && (
              <img
                src={signature}
                alt="Signature"
                className="mt-2 max-h-[100px]"
              />
            )}
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Summary</h2>
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>{currency} {calculateSubtotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Total Tax:</span>
              <span>{currency} {calculateTotalTax().toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Total:</span>
              <span>{currency} {calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ai" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-500" />
                AI Document Processing
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Upload an invoice document to automatically extract data using AI
              </p>
            </CardHeader>
            <CardContent>
              <DocumentUpload
                documentType="invoice"
                businessId={selectedBusiness?.id || 1}
                onExtractionComplete={handleExtractionComplete}
                className="w-full"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="extracted" className="space-y-6">
          {extractedData && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Extracted Data
                  </CardTitle>
                  <Button
                    onClick={applyExtractedData}
                    disabled={isApplyingExtraction}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {isApplyingExtraction ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Apply to Form
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Review the extracted data and apply it to your invoice form
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {extractedData.vendorName && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <label className="text-sm font-medium text-blue-800">Vendor/Customer</label>
                      <p className="text-blue-700">{extractedData.vendorName}</p>
                    </div>
                  )}
                  
                  {extractedData.invoiceNumber && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <label className="text-sm font-medium text-blue-800">Invoice Number</label>
                      <p className="text-blue-700">{extractedData.invoiceNumber}</p>
                    </div>
                  )}
                  
                  {extractedData.invoiceDate && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <label className="text-sm font-medium text-blue-800">Invoice Date</label>
                      <p className="text-blue-700">{extractedData.invoiceDate}</p>
                    </div>
                  )}
                  
                  {extractedData.totalAmount && (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <label className="text-sm font-medium text-green-800">Total Amount</label>
                      <p className="text-green-700 font-semibold">
                        {extractedData.currency || '$'}{extractedData.totalAmount}
                      </p>
                    </div>
                  )}
                  
                  {extractedData.taxAmount && (
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <label className="text-sm font-medium text-amber-800">Tax Amount</label>
                      <p className="text-amber-700">
                        {extractedData.currency || '$'}{extractedData.taxAmount}
                      </p>
                    </div>
                  )}
                  
                  {extractedData.vendorAddress && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="text-sm font-medium text-gray-800">Vendor Address</label>
                      <p className="text-gray-700">{extractedData.vendorAddress}</p>
                    </div>
                  )}
                </div>

                {extractedData.lineItems && extractedData.lineItems.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-800 mb-3">Line Items</h4>
                    <div className="space-y-2">
                      {extractedData.lineItems.map((item: any, index: number) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg border">
                          <div className="grid grid-cols-4 gap-2 text-sm">
                            <div>
                              <span className="font-medium">Description:</span>
                              <p className="text-gray-700">{item.description}</p>
                            </div>
                            <div>
                              <span className="font-medium">Quantity:</span>
                              <p className="text-gray-700">{item.quantity}</p>
                            </div>
                            <div>
                              <span className="font-medium">Unit Price:</span>
                              <p className="text-gray-700">{extractedData.currency || '$'}{item.unitPrice}</p>
                            </div>
                            <div>
                              <span className="font-medium">Total:</span>
                              <p className="text-gray-700 font-semibold">{extractedData.currency || '$'}{item.totalPrice}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <div className="hidden">
        <PrintableDocument
          ref={printableRef}
          document={{
            id: 'temp-id',
            number: invoiceNumber,
            type: 'invoice',
            date: new Date().toLocaleDateString('en-GB'),
            currency,
            items,
            status: 'pending',
            companyDetails,
            customerDetails: {
              name: `${customerDetails.firstName} ${customerDetails.lastName}`,
              address: `${customerDetails.street}, ${customerDetails.city}, ${customerDetails.state}, ${customerDetails.country}`,
              emailAddress: customerDetails.emailAddress,
              companyName: customerDetails.companyName
            },
            notes,
            terms,
            logo,
            signature,
            taxes: taxes.map(tax => ({
              taxName: tax.taxName,
              taxRate: tax.taxRate,
              taxAmount: tax.taxAmount,
            })),
            issueDate: issueDate.split('-').reverse().join('/'),
            dueDate: dueDate.split('-').reverse().join('/'),
            businessTaxInfos,
            customerTaxInfos
          }}
        />
      </div>
    </div>
  );
}